/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import Button from "@mui/material/Button";
import { NavigateFunction, useNavigate } from "react-router-dom";

import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";

import { Alert, Box, CircularProgress, Link, Tooltip, Typography } from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import DataTable from "../../components/table/DataTable";
import { deleteSite, updateSites } from "./workSitesFunctions";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Company, Effect, ServiceLocation } from "../../utils/Interfaces";
import { useWindowDimensions } from "../../utils/Hooks";
import { getStateMapping } from "../../utils/Functions";
import { StateMapping } from "../../utils/Constants";
import ConfirmDeleteModal from "../../components/confirmation/ConfirmDeleteModal";
import EditSiteModal from "./EditSiteModal";
import { selectExistingSites, selectExistingSitesLoaded } from "./workSitesSlice";
import CreateSiteModal from "./CreateSiteModal";
import { selectExistingCompanies, selectExistingCompaniesLoaded } from "../office-companies/companiesSlice";
import { updateCompanies } from "../office-companies/companiesFunctions";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../app/hooks";

const WorkSitesList = () => {
    const { height } = useWindowDimensions();

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

    const [toDeleteId, setToDeleteId] = useState<number>(-1);

    const [toDeleteCompanyName, setToDeleteCompanyName] = useState<string>("");
    const [toDeleteStoreNumber, setToDeleteStoreNumber] = useState<string>("");

    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

    const existingCompanies: Company[] = useAppSelector(selectExistingCompanies);
    const existingCompaniesLoaded: boolean = useAppSelector(selectExistingCompaniesLoaded);

    const existingSites: ServiceLocation[] = useAppSelector(selectExistingSites);
    const sitesLoaded: boolean = useAppSelector(selectExistingSitesLoaded);

    const [currentSite, setCurrentSite] = useState<ServiceLocation>({
        id: 0,
        street_address: "",
        city: "",
        state: "VA",
        zip_code: null,
        company: { name: "", id: 0, deletable: false },
        store_no: "",
        store_type: "",
        comments: null,
    });

    function clearCurrentSite(): void {
        setCurrentSite({
            id: 0,
            street_address: "",
            city: "",
            state: "",
            zip_code: null,
            company: { name: "", id: 0, deletable: false },
            store_no: "",
            store_type: "",
            comments: null,
        });
    }

    const navigate: NavigateFunction = useNavigate();
    const columns = [
        { field: "id", headerName: "id", filterable: false, sortable: true },
        {
            field: "Edit",
            headerName: "Edit",
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <IconButton
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            const street_address = params.row.street_address ?? "";
                            const city = params.row.city ?? "";

                            const state: StateMapping =
                                params.row.state === null
                                    ? { name: "Virginia", abbrev: "VA" }
                                    : getStateMapping(params.row.state);

                            const zip_code = params.row.zip_code ?? null;

                            setCurrentSite({
                                street_address: street_address,
                                city: city,
                                state: state.abbrev,
                                zip_code: zip_code,
                                company: params.row.company,
                                store_no: params.row.store_no,
                                id: parseInt(params.row.id),
                                store_type: params.row.store_type,
                                comments: params.row.comments,
                            });
                            setEditModalOpen(true);
                        }}
                        size="large"
                    >
                        <EditIcon />
                    </IconButton>
                );
            },
        },
        {
            field: "store_no",
            width: 150,
            headerName: "Store #",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.store_no;
            },
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link style={{ cursor: "pointer" }} onClick={() => navigate(`/work-sites/${params.row.id}`)}>
                        <Typography variant="overline" fontSize={14}>
                            {params.row.store_no}
                        </Typography>
                    </Link>
                );
            },
        },
        {
            field: "street_address",
            width: 320,
            headerName: "Street Address",
            sortable: true,
            filterable: true,
        },
        {
            width: 220,
            field: "city",
            headerName: "City",
            sortable: true,
            filterable: true,
        },
        {
            field: "state",
            headerName: "State",
            sortable: true,
            filterable: true,
        },
        {
            field: "company",
            headerName: "Company",
            sortable: true,
            filterable: true,
            valueGetter: (params: GridRenderCellParams) => {
                return params.row.company.name;
            },
            renderCell: (params: GridRenderCellParams) => {
                return params.row.company.name;
            },
        },
        {
            field: "zip_code",
            headerName: "Zip Code",
            sortable: true,
            filterable: true,
        },
        {
            field: "comments",
            headerName: "Site Comments",
            sortable: true,
            filterable: true,
            width: 350,
        },
        {
            field: "delete_site",
            width: 150,
            headerName: "Delete Site",
            sortable: true,
            filterable: true,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title="Delete Site">
                        <IconButton
                            onClick={() => {
                                setToDeleteId(params.row.id);
                                setToDeleteCompanyName(params.row.company.name);
                                setToDeleteStoreNumber(params.row.store_no);
                                setDeleteModalOpen(true);
                            }}
                        >
                            <DeleteIcon color="error" />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
    ];

    useEffect(() => {
        updateSites(dispatch, () => {});
        updateCompanies(dispatch, () => {});
    }, [dispatch]);

    if (existingCompaniesLoaded && existingCompanies.length === 0) {
        return (
            <Box>
                <StyledBreadcrumbs
                    paths={[
                        { text: "Techwise Portal", route: "/" },
                        { text: "Service", route: "/?section=office" },
                        { text: "Service Locations", route: "/work-sites" },
                    ]}
                />
                <Alert severity="error">
                    <Typography textAlign="center">
                        There are currently no companies created within the portal. All work sites must be assigned a parent
                        company. Please create a new company before managing work sites. Companies can be created and managed
                        through the&nbsp;
                        <Link sx={{ cursor: "pointer" }} onClick={() => navigate("/companies")}>
                            companies page
                        </Link>
                        .
                    </Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <ConfirmDeleteModal
                confirmText={`delete ${toDeleteCompanyName} #${toDeleteStoreNumber}`}
                handleClose={() => setDeleteModalOpen(false)}
                open={deleteModalOpen}
                description={
                    "You are about to delete the following site, all of its work orders, trips and assignment data. This cannot be undone."
                }
                onDelete={(modalCallback: Effect) => {
                    deleteSite(toDeleteId, dispatch, () => {
                        setToDeleteId(-1);
                        setToDeleteCompanyName("");
                        setToDeleteStoreNumber("");
                        updateSites(dispatch, () => {});
                        setDeleteModalOpen(false);
                        modalCallback();
                    });
                }}
            />

            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Service", route: "/?section=office" },
                    { text: "Service Locations", route: "/work-sites" },
                ]}
            />

            {(!sitesLoaded || !existingCompaniesLoaded) && (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            marginTop: "20vh",
                        }}
                    >
                        <CircularProgress size={40} />
                    </Box>
                </Box>
            )}

            {sitesLoaded && existingCompaniesLoaded && (
                <DataTable
                    tableName="WORK_SITES_LIST"
                    height={height - 170}
                    columns={columns}
                    data={existingSites}
                    allowRowSelected={false}
                >
                    <Button onClick={() => setCreateModalOpen(true)}>Add New Site</Button>
                </DataTable>
            )}

            {createModalOpen && (
                <CreateSiteModal
                    open={createModalOpen}
                    handleClose={() => {
                        setCreateModalOpen(false);
                        clearCurrentSite();
                    }}
                />
            )}

            {editModalOpen && (
                <EditSiteModal
                    open={editModalOpen}
                    handleClose={() => {
                        setEditModalOpen(false);
                        clearCurrentSite();
                    }}
                    site={currentSite}
                />
            )}
        </Box>
    );
};

export default WorkSitesList;
